import { Box, type SxProps, type Theme } from "@mui/material";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { GRID_FONT, NEUTRAL } from "../../../theme/tokens";
import { type CensusDisplayGroupPart, censusDisplayGroups, isUnassignedDisplayGroup } from "../../../utils/censusDisplayGroups";
import type { DepartmentLocation, LocationMode, StatsMode } from "../../../utils/models";

export type CensusBreakdownTooltipContentProps = {
	/** Headline lines shown above the breakdown, e.g. "Census: 84" or a manual-override pair. */
	/** `value` omitted renders the label alone, for a sentence that is not a "label: number" pair. */
	summaryLines: Array<{ label: string; value?: string | number }>;
	/** Which buckets to break the census into. `None` renders the summary only. */
	mode: LocationMode;
	/** The day's canonical dimension map — read it through `byDimensionsOf`, never raw. */
	byDimensions: Record<string, number>;
	/** Department total, used for locations that carry no unit/floor/room mapping. */
	totalCensus: number;
	locations: DepartmentLocation[];
	statsMode: StatsMode;
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

/**
 * The body of a census tooltip: a few summary lines, then the per-group census breakdown.
 *
 * Replaces three near-identical inline copies (the day-schedule header, the staffing day block and
 * the day-view census control) that had each drifted — different sections per mode, a hardcoded
 * `#ccc` divider in one and a theme token in another, and untranslated English labels throughout.
 *
 * The breakdown comes from `censusDisplayGroups`, the same helper the staff table's group headers
 * use, so a tooltip and the rows under it cannot show different numbers for the same bucket.
 */
/**
 * Stable React key for a part — its area, which is unique within a group by construction.
 *
 * Serialized rather than joined on a delimiter: unit and floor are free text from the EMR or the
 * location config, so a `|` inside a unit name would make `unit="A|B", floor=""` collide with
 * `unit="A", floor="B"` and let React drop one of two distinct rows. Same reason
 * `censusDimensionKey` escapes its separators. Matches `mappingSignature` in `censusDisplayGroups`.
 */
const partKey = (part: CensusDisplayGroupPart): string => JSON.stringify([part.unit, part.floor, part.rooms]);

/**
 * A part's area as human text: "Unit 2N · Floor 1st", "3 rooms", or the unmapped label.
 *
 * Built here rather than in `censusDisplayGroups` so the wording stays translatable — the util deals
 * in raw dimension values and never in prose.
 */
const partLabel = (part: CensusDisplayGroupPart, t: TFunction): string => {
	const segments: string[] = [];
	if (part.unit !== null) segments.push(t("census.dimension.unit", { value: part.unit }));
	if (part.floor !== null) segments.push(t("census.dimension.floor", { value: part.floor }));
	if (part.rooms.length > 0) segments.push(t("census.dimension.rooms", { count: part.rooms.length }));
	return segments.length > 0 ? segments.join(" · ") : t("census.dimension.noMapping");
};

export const CensusBreakdownTooltipContent = ({
	summaryLines,
	mode,
	byDimensions,
	totalCensus,
	locations,
	statsMode,
	sx,
	dataTestId = "census-breakdown-tooltip",
}: CensusBreakdownTooltipContentProps) => {
	const { t } = useTranslation();
	const groups = censusDisplayGroups(mode, byDimensions, totalCensus, locations, statsMode);

	return (
		<Box
			data-testid={dataTestId}
			sx={[{ display: "flex", flexDirection: "column", fontSize: GRID_FONT.MICRO }, ...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx])]}
		>
			{summaryLines.map((line) => (
				<Box key={line.label} data-testid={`${dataTestId}-summary`}>
					{line.value == null ? line.label : `${line.label}: ${line.value}`}
				</Box>
			))}

			{groups.length > 0 && (
				<Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${NEUTRAL[400]}` }} data-testid={`${dataTestId}-breakdown`}>
					<Box sx={{ fontWeight: 600 }}>{t(`census.breakdown.heading.${mode}`)}</Box>
					{groups.map((group) => (
						<Box key={group.key}>
							<Box data-testid={`${dataTestId}-group`}>
								{isUnassignedDisplayGroup(group.key) ? t("census.dimension.unassigned") : group.key}: {group.census}
							</Box>
							{/* Only Auto can span several areas, and then the header number is a sum whose origin is
							    not otherwise visible — so spell out the areas it came from. */}
							{group.parts.length > 1 &&
								group.parts.map((part) => (
									<Box key={partKey(part)} sx={{ pl: 1.5, opacity: 0.8 }} data-testid={`${dataTestId}-group-part`}>
										{partLabel(part, t)}: {part.census}
									</Box>
								))}
						</Box>
					))}
				</Box>
			)}
		</Box>
	);
};
